/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Report.jsx
 * Description  : Report page with filters and export
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getRecords, getReportExcel, getReportPdf, getReportCsv } from '../api';
import { useToast } from '../App';
import Paginator, { usePagination } from '../components/Paginator';

const THAI_MONTHS = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

function thaiDate(str) {
  if (!str) return '-';
  try {
    const [y, m, d] = str.split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
  } catch { return str; }
}

function downloadBlob(data, filename, mimeType) {
  const url = window.URL.createObjectURL(new Blob([data], { type: mimeType }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  window.URL.revokeObjectURL(url); document.body.removeChild(a);
}

export default function Report() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [sortField, setSortField] = useState(null);   // 'reserve_date' | 'cleared_date'
  const [sortDir, setSortDir] = useState('asc');      // 'asc' | 'desc'
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRecords('all');
      setRecords(res.data);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'โหลดข้อมูลไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const companies = useMemo(() => {
    const set = new Set(records.map((r) => r.company).filter(Boolean));
    return [...set].sort();
  }, [records]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let rows = records.filter((r) => {
      if (filterStatus === 'cleared' && r.cleared !== 2) return false;
      if (filterStatus === 'pending' && r.cleared === 2) return false;
      if (filterCompany !== 'all' && r.company !== filterCompany) return false;
      return true;
    });

    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortField] || '';
        const bv = b[sortField] || '';
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [records, filterStatus, filterCompany, sortField, sortDir]);

  const totalAmount = useMemo(
    () => filtered.reduce((s, r) => s + parseFloat(r.amount || 0), 0),
    [filtered]
  );
  const totalRemaining = useMemo(
    () => filtered.reduce((s, r) => s + Math.max(0, parseFloat(r.amount || 0) - parseFloat(r.cleared_amount || 0)), 0),
    [filtered]
  );
  const clearedCount = useMemo(() => filtered.filter((r) => r.cleared === 2).length, [filtered]);

  useEffect(() => { setPage(1); }, [filterStatus, filterCompany, sortField, sortDir, pageSize]);
  const { pageItems, safePage } = usePagination(filtered, pageSize, page);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      let res, filename, mime;
      if (type === 'excel') {
        res = await getReportExcel();
        filename = `csaf_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
        mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'pdf') {
        res = await getReportPdf();
        filename = `csaf_report_${new Date().toISOString().slice(0, 10)}.pdf`;
        mime = 'application/pdf';
      } else {
        res = await getReportCsv();
        filename = `csaf_report_${new Date().toISOString().slice(0, 10)}.csv`;
        mime = 'text/csv';
      }
      downloadBlob(res.data, filename, mime);
      addToast('success', `ดาวน์โหลด ${type.toUpperCase()} สำเร็จ`);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', `ดาวน์โหลด ${type.toUpperCase()} ไม่สำเร็จ`);
      }
    } finally {
      setDownloading('');
    }
  };

  const selectCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white';

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300">⇅</span>;
    return <span className="ml-1 text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thSort = 'px-4 py-3 text-center cursor-pointer select-none hover:bg-gray-100 transition-colors';

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'รายการทั้งหมด',       value: filtered.length, color: 'bg-primary text-white' },
          { label: 'เคลียร์แล้ว',          value: clearedCount,    color: 'bg-success text-white' },
          { label: 'ยอดรวมทั้งหมด (บาท)', value: totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }), color: 'bg-warning text-white' },
          { label: 'ยอดคงเหลือรวม (บาท)', value: totalRemaining.toLocaleString('th-TH', { minimumFractionDigits: 2 }), color: 'bg-orange-500 text-white' },
        ].map((item) => (
          <div key={item.label} className={`rounded-xl p-5 ${item.color} shadow-sm`}>
            <div className="text-2xl font-bold">{item.value}</div>
            <div className="text-sm opacity-90 mt-1">{item.label}</div>
          </div>
        ))}
      </div>

      {/* Export buttons */}
      <div className="flex flex-wrap gap-3">
        {[
          { type: 'excel', label: 'ดาวน์โหลด Excel', icon: '📊', cls: 'bg-green-600 hover:bg-green-700' },
          { type: 'pdf',   label: 'ดาวน์โหลด PDF',   icon: '📄', cls: 'bg-danger hover:bg-red-700' },
          { type: 'csv',   label: 'ดาวน์โหลด CSV',   icon: '📋', cls: 'bg-gray-500 hover:bg-gray-600' },
        ].map(({ type, label, icon, cls }) => (
          <button
            key={type}
            onClick={() => handleDownload(type)}
            disabled={!!downloading}
            className={`flex items-center gap-2 px-5 py-2.5 ${cls} text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60`}
          >
            {downloading === type
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : icon}
            {label}
          </button>
        ))}
      </div>

      {/* Filter + Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <h2 className="font-semibold text-gray-700 flex-1">รายการทั้งหมด ({filtered.length} รายการ)</h2>

          {/* Status filter */}
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectCls}>
            <option value="all">ทุกสถานะ</option>
            <option value="pending">รอดำเนินการ</option>
            <option value="cleared">เคลียร์แล้ว</option>
          </select>

          {/* Company filter */}
          <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className={selectCls}>
            <option value="all">ทุกบริษัท</option>
            {companies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">ไม่มีรายการ</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">ชื่อ / อีเมล</th>
                  <th className="px-4 py-3 text-left">บริษัท</th>
                  <th className="px-4 py-3 text-right">ยอดเงิน</th>
                  <th className="px-4 py-3 text-right">ยอดคงเหลือ</th>
                  <th className={thSort} onClick={() => handleSort('reserve_date')}>
                    วันที่สำรอง<SortIcon field="reserve_date" />
                  </th>
                  <th className="px-4 py-3 text-center">Alert 1</th>
                  <th className="px-4 py-3 text-center">Alert 2</th>
                  <th className="px-4 py-3 text-center">Alert 3</th>
                  <th className="px-4 py-3 text-center">สถานะ</th>
                  <th className={thSort} onClick={() => handleSort('cleared_date')}>
                    วันที่เคลียร์<SortIcon field="cleared_date" />
                  </th>
                  <th className="px-4 py-3 text-left">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageItems.map((r, idx) => (
                  <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${r.cleared ? 'bg-green-50' : ''}`}>
                    <td className="px-4 py-3 text-gray-400">{(safePage - 1) * pageSize + idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{r.name}</div>
                      <div className="text-xs text-gray-400">{r.email || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{r.company || '-'}</td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {parseFloat(r.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(() => {
                        const remaining = parseFloat(r.amount || 0) - parseFloat(r.cleared_amount || 0);
                        if (r.cleared === 2) {
                          return <span className="text-success font-medium">—</span>;
                        }
                        return (
                          <span className={`font-medium ${r.cleared === 1 ? 'text-orange-500' : 'text-gray-700'}`}>
                            {remaining.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{thaiDate(r.reserve_date)}</td>
                    {[r.alert1_sent, r.alert2_sent, r.alert3_sent].map((sent, i) => (
                      <td key={i} className="px-4 py-3 text-center">
                        <span className={`text-xs font-medium ${sent ? 'text-success' : 'text-gray-400'}`}>
                          {sent ? '✓ ส่งแล้ว' : '—'}
                        </span>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                        ${r.cleared === 2 ? 'bg-green-100 text-success'
                        : r.cleared === 1 ? 'bg-orange-100 text-orange-600'
                        : 'bg-yellow-100 text-warning'}`}>
                        {r.cleared === 2 ? 'เคลียร์แล้ว' : r.cleared === 1 ? 'เคลียร์บางส่วน' : 'รอดำเนินการ'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 text-xs">
                      {r.cleared_date ? thaiDate(r.cleared_date) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[220px] align-top">
                      {r.note ? <div className="whitespace-pre-wrap break-words">{r.note}</div> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator
              total={filtered.length} pageSize={pageSize} setPageSize={setPageSize}
              page={safePage} setPage={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
